import React, { useState, useEffect, useRef } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { COMPANY, SERVICES } from '../data/siteData'

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false)
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false)
  const dropdownRef = useRef(null)
  const location = useLocation()

  // Close mobile drawer on route change
  useEffect(() => {
    setIsOpen(false)
    setServicesDropdownOpen(false)
  }, [location.pathname])

  // Close desktop dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setServicesDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Prevent scroll when mobile drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const isServicesActive = [
    '/ydelser',
    '/nedgravning-af-trampolin',
    '/belaegning',
    '/fundament',
    '/gravearbejde'
  ].includes(location.pathname)

  return (
    <>
      <header className="sticky top-0 z-40 w-full bg-stone-700/95 backdrop-blur border-b border-stone-600 text-stone-100 shadow-md">
      {/* Top Bar with contact info */}
      <div className="border-b border-stone-700/80 bg-stone-800/70 py-1.5 px-4 text-xs">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center gap-2 text-stone-200">
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center gap-1.5 text-emerald-300 font-medium">
              <i className="fa-solid fa-location-dot" aria-hidden="true"></i>
              <span>Baseret i Hjortshøj – dækker Østjylland & Djursland</span>
            </span>
          </div>
          <div className="flex items-center gap-4 ml-auto">
            <a
              href={COMPANY.phoneHref}
              className="inline-flex items-center gap-1.5 hover:text-emerald-300 transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-emerald-300 text-[11px]" aria-hidden="true"></i>
              <span className="font-semibold text-white">{COMPANY.phone}</span>
            </a>
            <span className="text-stone-500 hidden sm:inline">|</span>
            <a
              href={`mailto:${COMPANY.email}`}
              className="hidden sm:inline-flex items-center gap-1.5 hover:text-emerald-300 transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope text-emerald-300 text-[11px]" aria-hidden="true"></i>
              <span>{COMPANY.email}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        {/* Brand Logo */}
        <Link
          to="/"
          className="group flex items-center gap-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded-sm"
        >
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=360,quality=90,format=auto/assets/385186cd-968f-4839-8612-1c0b142a077c/jt-logo-farve.png"
            alt="JT Knudsen v/ Jonas Knudsen - Entreprenør i Hjortshøj og Aarhus"
            className="h-18 sm:h-20 w-auto max-h-20 object-contain"
          />
        </Link>

        {/* Desktop Navigation (Visible only at >= 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center space-x-1">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `px-3.5 py-2 text-sm font-medium rounded-md transition-colors ${
                isActive
                  ? 'text-emerald-300 bg-stone-600 font-semibold'
                  : 'text-stone-200 hover:text-white hover:bg-stone-600/60'
              }`
            }
          >
            Forside
          </NavLink>

          {/* Ydelser Dropdown */}
          <div
            className="relative"
            ref={dropdownRef}
            onMouseEnter={() => setServicesDropdownOpen(true)}
            onMouseLeave={() => setServicesDropdownOpen(false)}
          >
            <button
              type="button"
              onClick={() => setServicesDropdownOpen(!servicesDropdownOpen)}
              aria-expanded={servicesDropdownOpen}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer ${
                isServicesActive
                  ? 'text-emerald-300 bg-stone-600 font-semibold'
                  : 'text-stone-200 hover:text-white hover:bg-stone-600/60'
              }`}
            >
              <span>Ydelser</span>
              <i
                className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                  servicesDropdownOpen ? 'rotate-180 text-emerald-300' : ''
                }`}
                aria-hidden="true"
              ></i>
            </button>

            {/* Dropdown Menu */}
            {servicesDropdownOpen && (
              <div className="absolute top-full left-0 w-72 pt-2 z-50">
                <div className="bg-stone-900 border border-stone-700/80 rounded-lg shadow-2xl p-2.5 backdrop-blur">
                  <div className="text-[11px] font-bold text-emerald-400 uppercase tracking-wider px-3 py-1.5 border-b border-stone-800">
                    Vores arbejdsområder
                  </div>
                  <div className="mt-1.5 space-y-1">
                    <NavLink
                      to="/belaegning"
                      className={({ isActive }) =>
                        `flex items-start gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                          isActive
                            ? 'bg-[#015205]/30 text-emerald-400 font-semibold'
                            : 'text-stone-300 hover:bg-stone-800 hover:text-white'
                        }`
                      }
                    >
                      <i className="fa-solid fa-shapes mt-1 text-emerald-500 text-sm" aria-hidden="true"></i>
                      <div>
                        <div className="font-medium text-stone-100">Belægning</div>
                        <div className="text-xs text-stone-400 font-normal mt-0.5">
                          Terrasser, gangarealer & indkørsler
                        </div>
                      </div>
                    </NavLink>

                    <NavLink
                      to="/fundament"
                      className={({ isActive }) =>
                        `flex items-start gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                          isActive
                            ? 'bg-[#015205]/30 text-emerald-400 font-semibold'
                            : 'text-stone-300 hover:bg-stone-800 hover:text-white'
                        }`
                      }
                    >
                      <i className="fa-solid fa-cubes-stacked mt-1 text-emerald-500 text-sm" aria-hidden="true"></i>
                      <div>
                        <div className="font-medium text-stone-100">Fundament & sokkel</div>
                        <div className="text-xs text-stone-400 font-normal mt-0.5">
                          Tilbygninger, skure & støbearbejde
                        </div>
                      </div>
                    </NavLink>

                    <NavLink
                      to="/gravearbejde"
                      className={({ isActive }) =>
                        `flex items-start gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                          isActive
                            ? 'bg-[#015205]/30 text-emerald-400 font-semibold'
                            : 'text-stone-300 hover:bg-stone-800 hover:text-white'
                        }`
                      }
                    >
                      <i className="fa-solid fa-trowel-bricks mt-1 text-emerald-500 text-sm" aria-hidden="true"></i>
                      <div>
                        <div className="font-medium text-stone-100">Gravearbejde</div>
                        <div className="text-xs text-stone-400 font-normal mt-0.5">
                          Maskingravning & jordflytning
                        </div>
                      </div>
                    </NavLink>

                    <NavLink
                      to="/nedgravning-af-trampolin"
                      className={({ isActive }) =>
                        `flex items-start gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                          isActive
                            ? 'bg-[#015205]/30 text-emerald-400 font-semibold'
                            : 'text-stone-300 hover:bg-stone-800 hover:text-white'
                        }`
                      }
                    >
                      <i className="fa-solid fa-circle-nodes mt-1 text-emerald-500 text-sm" aria-hidden="true"></i>
                      <div>
                        <div className="font-medium text-stone-100">Nedgravning af trampolin</div>
                        <div className="text-xs text-stone-400 font-normal mt-0.5">
                          Præcis udgravning & integreret løsning
                        </div>
                      </div>
                    </NavLink>
                  </div>

                  <div className="mt-2 pt-2 border-t border-stone-800">
                    <NavLink
                      to="/ydelser"
                      className="block px-3 py-1.5 text-xs text-emerald-400 hover:text-emerald-300 font-medium"
                    >
                      Se alle 4 ydelser &rarr;
                    </NavLink>
                  </div>
                </div>
              </div>
            )}
          </div>

          <NavLink
            to="/om-jt-knudsen"
            className={({ isActive }) =>
              `px-3.5 py-2 text-sm font-medium rounded-md transition-colors ${
                isActive
                  ? 'text-emerald-300 bg-stone-600 font-semibold'
                  : 'text-stone-200 hover:text-white hover:bg-stone-600/60'
              }`
            }
          >
            Om JT Knudsen
          </NavLink>

          <NavLink
            to="/galleri"
            className={({ isActive }) =>
              `px-3.5 py-2 text-sm font-medium rounded-md transition-colors ${
                isActive
                  ? 'text-emerald-300 bg-stone-600 font-semibold'
                  : 'text-stone-200 hover:text-white hover:bg-stone-600/60'
              }`
            }
          >
            Galleri
          </NavLink>

          <NavLink
            to="/kontakt"
            className={({ isActive }) =>
              `px-3.5 py-2 text-sm font-medium rounded-md transition-colors ${
                isActive
                  ? 'text-emerald-300 bg-stone-600 font-semibold'
                  : 'text-stone-200 hover:text-white hover:bg-stone-600/60'
              }`
            }
          >
            Kontakt
          </NavLink>
        </nav>

        {/* Desktop Quick Actions (>= 1200px) */}
        <div className="hidden min-[1200px]:flex items-center gap-3">
          <a
            href={COMPANY.phoneHref}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md bg-stone-600 hover:bg-stone-500 text-white text-sm font-semibold border border-stone-500 hover:border-stone-400 transition-colors whitespace-nowrap"
          >
            <i className="fa-solid fa-phone text-emerald-300 text-xs" aria-hidden="true"></i>
            <span>{COMPANY.phone}</span>
          </a>
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#015205] hover:bg-[#026b07] text-white text-sm font-bold shadow transition-all hover:shadow-[#015205]/30 whitespace-nowrap"
          >
            <span>Få et uforpligtende tilbud</span>
            <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
          </Link>
        </div>

        {/* Burger Button (Only visible under 1200px as instructed) */}
        <div className="flex min-[1200px]:hidden items-center gap-2">
          <a
            href={COMPANY.phoneHref}
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-stone-600 text-white text-xs font-semibold border border-stone-500 whitespace-nowrap mr-1"
            aria-label="Ring til Jonas Knudsen"
          >
            <i className="fa-solid fa-phone text-emerald-300" aria-hidden="true"></i>
            <span>{COMPANY.phone}</span>
          </a>

          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="p-2.5 rounded-md text-stone-100 hover:text-white hover:bg-stone-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer"
            aria-label={isOpen ? 'Luk menu' : 'Åbn menu'}
            aria-expanded={isOpen}
          >
            <i className={`fa-solid ${isOpen ? 'fa-xmark text-2xl' : 'fa-bars text-2xl'}`} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      </header>

      {/* Fullscreen Mobile Drawer (outside sticky header to avoid stacking context clipping) */}
      {isOpen && (
        <div
          id="mobile-drawer-menu"
          style={{ height: '100dvh', maxHeight: '100dvh' }}
          className="fixed inset-0 z-[9999] min-[1200px]:hidden bg-stone-900 text-stone-100 flex flex-col shadow-2xl"
        >
          {/* Top header inside drawer with branding & explicit close button */}
          <div className="flex items-center justify-between px-4 sm:px-6 h-20 border-b border-stone-800 bg-stone-950 shrink-0">
            <Link
              to="/"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 text-left"
            >
              <div className="w-10 h-10 rounded-lg bg-[#015205] flex items-center justify-center text-white font-black text-lg shadow-inner">
                JT
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-base tracking-tight text-stone-100">
                  JT Knudsen
                </span>
                <span className="text-[11px] text-stone-400 uppercase tracking-wider font-medium">
                  v/ Jonas Knudsen
                </span>
              </div>
            </Link>

            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="p-3 rounded-lg text-stone-200 hover:text-white bg-stone-800 hover:bg-stone-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer"
              aria-label="Luk menu"
            >
              <i className="fa-solid fa-xmark text-2xl text-emerald-400" aria-hidden="true"></i>
            </button>
          </div>

          {/* Scrollable menu body */}
          <div
            id="mobile-drawer-scroll-body"
            className="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-6 py-6 space-y-5 touch-pan-y"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="space-y-1.5">
              <NavLink
                to="/"
                end
                onClick={() => setIsOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl text-lg font-bold transition-colors ${
                    isActive ? 'bg-[#015205]/30 text-emerald-400' : 'text-stone-200 hover:bg-stone-800'
                  }`
                }
              >
                Forside
              </NavLink>

              {/* Mobile Services Accordion Box */}
              <div className="rounded-xl bg-stone-950/80 p-3.5 border border-stone-800 my-2.5">
                <div className="px-2 py-1 text-xs font-bold uppercase tracking-wider text-emerald-400 flex items-center justify-between">
                  <span>Ydelser</span>
                  <Link
                    to="/ydelser"
                    onClick={() => setIsOpen(false)}
                    className="text-stone-400 hover:text-emerald-400 text-xs underline font-medium"
                  >
                    Se alle ydelser
                  </Link>
                </div>
                <div className="mt-2.5 space-y-1.5">
                  {SERVICES.map((s) => (
                    <NavLink
                      key={s.path}
                      to={s.path}
                      onClick={() => setIsOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3.5 py-3 rounded-lg text-base font-medium transition-colors ${
                          isActive
                            ? 'bg-[#015205]/30 text-emerald-400 font-bold'
                            : 'text-stone-300 hover:bg-stone-800 hover:text-white'
                        }`
                      }
                    >
                      <i className={`${s.icon} text-emerald-500 w-5 text-center text-base`} aria-hidden="true"></i>
                      <span>{s.title}</span>
                    </NavLink>
                  ))}
                </div>
              </div>

              <NavLink
                to="/om-jt-knudsen"
                onClick={() => setIsOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl text-lg font-bold transition-colors ${
                    isActive ? 'bg-[#015205]/30 text-emerald-400' : 'text-stone-200 hover:bg-stone-800'
                  }`
                }
              >
                Om JT Knudsen
              </NavLink>

              <NavLink
                to="/galleri"
                onClick={() => setIsOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl text-lg font-bold transition-colors ${
                    isActive ? 'bg-[#015205]/30 text-emerald-400' : 'text-stone-200 hover:bg-stone-800'
                  }`
                }
              >
                Galleri
              </NavLink>

              <NavLink
                to="/kontakt"
                onClick={() => setIsOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl text-lg font-bold transition-colors ${
                    isActive ? 'bg-[#015205]/30 text-emerald-400' : 'text-stone-200 hover:bg-stone-800'
                  }`
                }
              >
                Kontakt
              </NavLink>
            </div>

            {/* Bottom Actions inside drawer */}
            <div className="pt-6 border-t border-stone-800 space-y-3.5 pb-12">
              <a
                href={COMPANY.phoneHref}
                className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl bg-stone-800 hover:bg-stone-700 text-stone-100 font-bold text-base border border-stone-700 whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-emerald-400 text-lg" aria-hidden="true"></i>
                <span>Ring på {COMPANY.phone}</span>
              </a>

              <Link
                to="/kontakt"
                onClick={() => setIsOpen(false)}
                className="w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl bg-[#015205] hover:bg-[#026b07] text-white font-black text-base shadow-lg"
              >
                <span>Få et uforpligtende tilbud</span>
                <i className="fa-solid fa-arrow-right text-sm" aria-hidden="true"></i>
              </Link>

              <div className="text-center pt-3 text-xs text-stone-400 leading-relaxed">
                <p className="font-medium text-stone-300">{COMPANY.address}</p>
                <p className="mt-1">{COMPANY.email}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
